import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import useSSE from '../../../../hooks/useSSE';
import PainelColeta from './PainelColeta';
import VisualizadorColetas from './VisualizadorColetas';
import { SUPERFICIE, fmtData, getItem } from './estilo';

const ROTA = '/admin/snapshot';

function ehLink(v) {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

/**
 * Uma janela de manutencao: o contexto no topo, as coletas dela embaixo.
 *
 * Tem rota propria de proposito. A queixa era "nao sei se estou na sessao", e
 * sessao sem URL nao e um lugar — some no F5, nao abre em outra aba, nao vai
 * colada num chamado. A comparacao tambem vive na URL, entao mandar
 * "olha o que mudou" para o colega e copiar o endereco.
 */
export default function SessaoPage() {
  const { id } = useParams();
  const navegar = useNavigate();
  const notify = useNotify();
  const [, setParams] = useSearchParams();

  const empresa = getItem(localStorage, 'hermes')?.empresa || null;

  const [sessao, setSessao] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [hosts, setHosts] = useState([]);


  const [runAtivo, setRunAtivo] = useState(null);
  const [statusAtivo, setStatusAtivo] = useState(null);
  const [iniciadoEm, setIniciadoEm] = useState(null);
  const [retentando, setRetentando] = useState(false);
  const [coletando, setColetando] = useState(false);
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false);
  const [encerrando, setEncerrando] = useState(false);


  useEffect(() => {
    document.title = sessao?.name
      ? `IPV7 Hermes - ${sessao.name}`
      : 'IPV7 Hermes - Sessão';
  }, [sessao]);

  const carregarStatus = useCallback(async (runId) => {
    try {
      const res = await api.get(`${ROTA}/run/${runId}/status`, { params: { empresa } });
      if (res.data?.data) setStatusAtivo(res.data.data);
    } catch {
      /* o poll tenta de novo */
    }
  }, [empresa]);

  const carregarSessao = useCallback(async () => {
    if (!empresa || !id) return null;
    try {
      const res = await api.get(`${ROTA}/session/${id}/detail`, { params: { empresa } });
      if (res.data?.error) {
        notify.setNotify({ open: true, severity: 'error', message: res.data.error });
        return null;
      }
      const d = res.data?.data || null;
      setSessao(d);
      return d;
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao carregar a sessão' });
      return null;
    } finally {
      setCarregando(false);
    }
    // notify vem de um Context estável.
  }, [empresa, id]);

  useEffect(() => {
    carregarSessao();
    api.post('/admin/hermes/host/', { limit: 1000, offset: 0, empresa })
      .then((r) => setHosts(r.data?.data || []))
      .catch(() => { });
  }, [carregarSessao, empresa]);

  // Reata a coleta em andamento ao abrir a página.
  //
  // Sem isto, o painel de progresso só existia para quem tinha clicado em
  // "Coletar agora" naquela aba: um F5, outra aba, ou o colega do NOC viam a
  // sessão sem sinal nenhum de que havia coleta rodando naquele instante.
  useEffect(() => {
    if (!sessao?.coletas?.length || runAtivo) return;
    const rodando = sessao.coletas.find(
      (c) => c.status === 'running' || c.status === 'pending'
    );
    if (rodando) {
      setRunAtivo({ run_id: rodando.run_id });
      // O cronômetro parte de quando a coleta começou de verdade, não de agora.
      setIniciadoEm(rodando.created_at ? new Date(rodando.created_at).getTime() : null);
      carregarStatus(rodando.run_id);
    }
  }, [sessao, runAtivo, carregarStatus]);

  const streamUrl = useMemo(() => {
    if (!runAtivo?.run_id) return null;
    if (statusAtivo && (statusAtivo.status === 'done' || statusAtivo.status === 'error')) return null;
    return `${ROTA}/run/${runAtivo.run_id}/stream?empresa=${empresa}`;
  }, [runAtivo, statusAtivo, empresa]);

  // O terceiro argumento e obrigatorio na pratica: sem ele `enabled` fica true e
  // o hook tenta ouvir mesmo com streamUrl nulo, que e o estado normal da tela
  // quando nenhuma coleta esta rodando.
  const { data: sseData } = useSSE(streamUrl, {}, !!streamUrl);

  useEffect(() => {
    if (!sseData) return;
    if (String(sseData.capture_id) === String(runAtivo?.run_id)) carregarStatus(runAtivo.run_id);
  }, [sseData]);

  useEffect(() => {
    const rodando = statusAtivo
      ? statusAtivo.status !== 'done' && statusAtivo.status !== 'error'
      : !!runAtivo;
    if (!runAtivo?.run_id || !rodando) return undefined;
    const t = setInterval(() => carregarStatus(runAtivo.run_id), 3000);
    return () => clearInterval(t);
  }, [runAtivo, statusAtivo, carregarStatus]);

  useEffect(() => {
    if (statusAtivo?.status === 'done' || statusAtivo?.status === 'error') carregarSessao();
  }, [statusAtivo?.status, carregarSessao]);

  const hostsPorId = useMemo(
    () => Object.fromEntries(hosts.map((h) => [h.id, h])),
    [hosts]
  );


  const coletar = async () => {
    setColetando(true);
    try {
      const res = await api.post(`${ROTA}/run/collect`, { empresa, session_id: Number(id) });
      if (res.data?.error) {
        notify.setNotify({ open: true, severity: 'error', message: res.data.error });
        return;
      }
      const d = res.data.data;
      setRunAtivo(d);
      setStatusAtivo(null);
      setIniciadoEm(Date.now());
      carregarStatus(d.run_id);
      carregarSessao();
      notify.setNotify({
        open: true,
        severity: d.skipped?.length ? 'warning' : 'success',
        message: d.skipped?.length
          ? `${d.enqueued} host(s) enfileirado(s), ${d.skipped.length} pulado(s)`
          : `Coleta #${d.run_id} iniciada em ${d.enqueued} host(s)`,
      });
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao coletar' });
    } finally {
      setColetando(false);
    }
  };

  const retentar = async (hostIds) => {
    if (!statusAtivo?.run_id) return;
    setRetentando(true);
    try {
      const res = await api.post(`${ROTA}/run/${statusAtivo.run_id}/retry`, {
        empresa, host_ids: hostIds || null,
      });
      if (res.data?.error) {
        notify.setNotify({ open: true, severity: 'error', message: res.data.error });
        return;
      }
      const d = res.data.data;
      notify.setNotify({
        open: true,
        severity: d.enqueued ? 'success' : 'info',
        message: d.aviso || `${d.enqueued} host(s) reenfileirado(s)`,
      });
      carregarStatus(statusAtivo.run_id);
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao retentar' });
    } finally {
      setRetentando(false);
    }
  };

  const encerrar = async () => {
    setEncerrando(true);
    try {
      const res = await api.post(`${ROTA}/session/${id}/close`, { empresa });
      if (res.data?.error) {
        // Recusa esperada — coleta em andamento. O diálogo fica aberto: fechá-lo
        // junto com o aviso faria o operador ler a mensagem sem ter mais nada na
        // tela ligando o texto ao botão que ele acabou de clicar.
        notify.setNotify({ open: true, severity: 'warning', message: res.data.error });
        return;
      }
      notify.setNotify({ open: true, severity: 'success', message: 'Sessão encerrada' });
      setConfirmarEncerrar(false);
      carregarSessao();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao encerrar' });
    } finally {
      setEncerrando(false);
    }
  };





  const coletas = sessao?.coletas || [];


  if (carregando) {
    return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  }

  if (!sessao) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Sessão não encontrada.</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navegar('/admin/hermes/snapshot')}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, minHeight: '100%', bgcolor: '#f1f5f9' }}>
      {/* Cabeçalho: é a moldura que diz "você está DENTRO desta janela". */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, ...SUPERFICIE }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Tooltip title="Voltar para as sessões">
            <IconButton size="small" onClick={() => navegar('/admin/hermes/snapshot')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6">{sessao.name}</Typography>
              <Chip
                size="small"
                icon={sessao.aberta ? undefined : <LockIcon sx={{ fontSize: 14 }} />}
                label={sessao.aberta ? 'aberta' : 'encerrada'}
                sx={{
                  height: 21, color: '#fff',
                  bgcolor: sessao.aberta ? '#16a085' : '#64748b',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />
              {sessao.ticket && (
                ehLink(sessao.ticket) ? (
                  <Button
                    size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    href={sessao.ticket} target="_blank" rel="noopener noreferrer"
                  >
                    chamado
                  </Button>
                ) : (
                  <Chip size="small" label={`chamado ${sessao.ticket}`}
                    sx={{ height: 20, bgcolor: '#f1f5f9', color: '#475569' }} />
                )
              )}
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                aberta em {fmtData(sessao.created_at)}
                {sessao.closed_at ? ` · encerrada em ${fmtData(sessao.closed_at)}` : ''}
              </Typography>
            </Stack>

            {sessao.description && (
              <Box sx={{ mt: 1, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1, borderLeft: '3px solid #cbd5e1' }}>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {sessao.description}
                </Typography>
              </Box>
            )}
          </Box>

          <Stack spacing={1} alignItems="flex-end">
            {sessao.aberta && (
              <>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={coletar}
                  disabled={coletando}
                >
                  Coletar agora
                </Button>
                <Button
                  size="small"
                  startIcon={<LockIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setConfirmarEncerrar(true)}
                >
                  encerrar sessão
                </Button>
              </>
            )}
            <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />} onClick={carregarSessao}>
              atualizar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {statusAtivo && (
        <PainelColeta
          rotulo={`#${statusAtivo.run_id}`}
          progresso={statusAtivo}
          hostsPorId={hostsPorId}
          empresa={empresa}
          iniciadoEm={iniciadoEm}
          onRetentar={retentar}
          retentando={retentando}
        />
      )}

      {!coletas.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhuma coleta ainda nesta sessão.
          {sessao.aberta ? ' Clique em “Coletar agora”.' : ''}
        </Alert>
      )}

      {!!coletas.length && (
        <VisualizadorColetas
          coletas={coletas}
          hostsPorId={hostsPorId}
          empresa={empresa}
          aoTrocarPar={(a, b) => setParams({ a: String(a), b: String(b) })}
        />
      )}

      {/* Confirmação de encerramento.
        *
        * O botão só ligava `confirmarEncerrar` e não havia diálogo nenhum lendo
        * esse estado: clicar não fazia absolutamente nada, sem erro no console
        * nem requisição — o tipo de falha que parece "travou" para quem usa.
        *
        * Encerrar é do operador, nunca de timeout, e não tem volta pela tela:
        * por isso confirma antes em vez de agir no primeiro clique.
        */}
      <Dialog
        open={confirmarEncerrar}
        onClose={() => setConfirmarEncerrar(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: SUPERFICIE }}
      >
        <DialogTitle>Encerrar sessão</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{sessao?.name}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            As coletas já feitas <strong>continuam guardadas</strong> e a comparação
            segue disponível. O que muda é que a sessão para de aceitar coletas
            novas.
          </Alert>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            {coletas.length
              ? `${coletas.length} coleta(s) nesta janela.`
              : 'Nenhuma coleta foi feita nesta janela.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarEncerrar(false)}>Cancelar</Button>
          <Button variant="contained" onClick={encerrar} disabled={encerrando}>
            {encerrando ? 'Encerrando…' : 'Encerrar'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
